/* eslint-disable react/jsx-no-useless-fragment */
import React, { useMemo, useState } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';
import styled from 'styled-components';
import { number, object, string } from 'yup';

import { Button } from 'src/app/components/lib/button';
import { DropdownSingle } from 'src/app/components/lib/dropdown';
import { FormSection } from 'src/app/components/lib/form/form';
import { Input, InputAdornment } from 'src/app/components/lib/input';
import { Toggle } from 'src/app/components/lib/toggle';
import { LD } from 'src/app/constants/launch-darkly-flags';
import { useLDFlag } from 'src/app/hooks/use-ld-flag';
import { pushRedirectPathOptions } from 'src/app/pages/marketing/campaigns/marketing-campaign-builder/shared-slides/campaign-setup-and-editor-builder/cards/push-card';
import { SendTestEmailModal } from 'src/app/pages/marketing/campaigns/marketing-campaign-builder/shared-slides/campaign-setup-and-editor-builder/modals/send-test-email-modal';
import { SendTestPushModal } from 'src/app/pages/marketing/campaigns/marketing-campaign-builder/shared-slides/campaign-setup-and-editor-builder/modals/send-test-push-modal';
import { SendTestSMSModal } from 'src/app/pages/marketing/campaigns/marketing-campaign-builder/shared-slides/campaign-setup-and-editor-builder/modals/send-test-sms-modal';
import {
  ButtonMarginLeft,
  NoContentBlock,
  SideBarSectionHeader,
  SideBarSectionHeaderContainer,
  SummarySidebarSection,
} from 'src/app/pages/marketing/workflows/workflow-campaign-builder/summary-sidebar/styles';
import { useGetSendersQuery } from 'src/app/queries/email-marketing/get-senders';
import { useGetSavedEmailQuery } from 'src/app/queries/email-marketing/get-template';
import { useSendTestEmailMutation } from 'src/app/queries/email-marketing/send-test-email';
import { useSaveWorkflowCampaignMutation } from 'src/app/queries/marketing/upsert-workflow-campaign';
import { useSendTestPushMutation } from 'src/app/queries/push-marketing/send-test-push';
import { useGetSMSTemplatesQuery } from 'src/app/queries/sms-marketing/get-sms-templates';
import { useSendTestSmsMutation } from 'src/app/queries/sms-marketing/send-test-sms';
import { useGetLeafbuyerMarketingConfigsQuery } from 'src/app/queries/sms-marketing/upsert-leafbuyer-marketing-config';
import { useNotificationActions } from 'src/app/state/notifications';
import { userDispensariesAtom } from 'src/app/state/user-dispensaries';

import { isEmailEditorEnabled } from '../../slides/workflow-editor/atoms/workflow-and-email-editor-atoms';
import { ActionTypeEnum } from '../../slides/workflow-editor/models';
import { useWorkflowBuilderContext } from '../../wizard/builder-context';
import { getSaveWorkflowCampaignPayload, validateWithSchema } from '../../wizard/helpers';

import type { ActionNodeData } from '../../slides/workflow-editor/models';
import type {
  EmailBuilderState,
  PointBoostBuilderState,
  PushBuilderState,
  SMSBuilderState,
} from 'src/app/pages/marketing/campaigns/marketing-campaign-builder/builder-context';
import type { Sender } from 'src/app/queries/email-marketing/get-senders';
import type { ApiErrorResponse } from 'src/app/queries/marketing/upsert-campaign';

export const actionSchema = object({
  actionType: string().required().label('Action type'),
  config: object()
    .when('actionType', {
      is: ActionTypeEnum.SEND_EMAIL,
      then: object({
        emailConfig: object({
          subjectLine: string().nullable().required().label('Subject'),
          previewText: string().nullable().required().label('Preview text'),
          senderEmail: string()
            .nullable()
            .when('senderRequired', {
              is: true,
              then: string().required().label('Sender email'),
            }),
          metaSenderEmail: string()
            .nullable()
            .when('senderRequired', {
              is: true,
              then: string().required().label('Sender email'),
            }),
        })
          .required()
          .label('Email config'),
        savedTemplateId: number().required().label('Email template'),
      }),
    })
    .when('actionType', {
      is: ActionTypeEnum.SEND_PUSH_NOTIFICATION,
      then: object({
        pushConfig: object({
          title: string().nullable().required().label('Title'),
          body: string().nullable().required().label('Body'),
          redirectPath: string().nullable().required().label('App landing page'),
        })
          .required()
          .label('Push config'),
      }),
    })
    .when('actionType', {
      is: ActionTypeEnum.SEND_SMS,
      then: object({
        smsConfig: object({
          leafbuyerSMSMarketingConfigId: number().required().label('Brand'),
        })
          .required()
          .label('SMS config'),
        savedTemplateId: number().required().label('SMS template'),
      }),
    })
    .when('actionType', {
      is: ActionTypeEnum.GRANT_POINTS,
      then: object({
        pointBoostConfig: object({
          points: number().required().min(1).max(1000000).label('Point boost'),
        }),
      }),
    }),
});

export function ActionSection({ nodeData, selectedNodeId }: { nodeData: ActionNodeData; selectedNodeId: string }) {
  const notifications = useNotificationActions();

  // state
  const [builderState, dispatch] = useWorkflowBuilderContext();
  const { selectedNodeIsNew, marketingCampaignId } = builderState;
  const [showSendTestEmailModal, setShowSendTestEmailModal] = useState(false);
  const [showSendTestSmsModal, setShowSendTestSmsModal] = useState(false);
  const [showSendTestPushModal, setShowSendTestPushModal] = useState(false);
  const useMessageLevelUtmFields = useLDFlag(LD.USE_MESSAGE_LEVEL_UTM_FIELDS);

  const { locProfile } = useRecoilValue(userDispensariesAtom);
  const [, setShowEditor] = useRecoilState(isEmailEditorEnabled);

  // switches
  const isEmailEnabled = locProfile.UseEmailMarketing;
  const isSMSEnabled = locProfile.UseSMSMarketing;
  const isPushEnabled = locProfile.UsePushMarketing;
  const showLeafLogixLoyalty = locProfile.ShowLeafLogixLoyalty;

  // queries
  const { data: senders, isLoading: sendersLoading } = useGetSendersQuery();
  const { mutate: sendTestEmailMutator } = useSendTestEmailMutation();
  const { mutate: sendTestSmsMutator } = useSendTestSmsMutation();
  const { mutate: sendTestPushMutator } = useSendTestPushMutation();
  const { mutateAsync: saveWorkflowCampaign } = useSaveWorkflowCampaignMutation();
  const { data: leafbuyerSMSMarketingConfigs } = useGetLeafbuyerMarketingConfigsQuery();

  const leafbuyerSMSMarketingConfigId = nodeData.config.smsConfig?.leafbuyerSMSMarketingConfigId;
  const configId = leafbuyerSMSMarketingConfigId || null;
  const { data: smsTemplates } = useGetSMSTemplatesQuery(configId);

  // Determine if we should show a dropdown or a disabled input for the sender
  const shouldShowSenderDropdown = useMemo(() => senders?.AllSenders && senders.AllSenders.length > 1, [senders]);
  const savedTemplateId = nodeData?.config?.savedTemplateId;

  const { data: savedEmailContent } = useGetSavedEmailQuery({
    templateId: savedTemplateId,
  });

  const validationErrors = useMemo(
    () => validateWithSchema(actionSchema, nodeData, selectedNodeIsNew),
    [nodeData, selectedNodeIsNew]
  );

  const setEmailConfig = (emailConfig: Partial<EmailBuilderState>) => {
    dispatch({
      type: 'set-node-data',
      payload: {
        nodeId: selectedNodeId,
        value: {
          config: {
            ...nodeData.config,
            emailConfig: {
              ...nodeData.config.emailConfig,
              ...emailConfig,
            },
          },
        },
      },
    });
  };

  const setPushConfig = (pushConfig: Partial<PushBuilderState>) => {
    dispatch({
      type: 'set-node-data',
      payload: {
        nodeId: selectedNodeId,
        value: {
          config: {
            ...nodeData.config,
            pushConfig: {
              ...nodeData.config.pushConfig,
              ...pushConfig,
            },
          },
        },
      },
    });
  };

  const setSmsConfig = (smsConfig: Partial<SMSBuilderState>) => {
    dispatch({
      type: 'set-node-data',
      payload: {
        nodeId: selectedNodeId,
        value: {
          config: {
            ...nodeData.config,
            smsConfig: {
              ...nodeData.config.smsConfig,
              ...smsConfig,
            },
          },
        },
      },
    });
  };

  const setPointBoostConfig = (pointBoostConfig: Partial<PointBoostBuilderState>) => {
    dispatch({
      type: 'set-node-data',
      payload: {
        nodeId: selectedNodeId,
        value: {
          config: {
            ...nodeData.config,
            pointBoostConfig: {
              ...nodeData.config.pointBoostConfig,
              ...pointBoostConfig,
            },
          },
        },
      },
    });
  };

  const setSubjectLine = (subjectLine: string) => {
    if (nodeData.actionType === ActionTypeEnum.SEND_EMAIL) {
      setEmailConfig({
        subjectLine,
      });
    }
  };

  const setPreviewText = (previewText: string) => {
    if (nodeData.actionType === ActionTypeEnum.SEND_EMAIL) {
      setEmailConfig({
        previewText,
      });
    }
  };

  const setUtmTerm = (utmTerm: string) => {
    if (nodeData.actionType === ActionTypeEnum.SEND_EMAIL) {
      setEmailConfig({
        utmTerm,
      });
    } else if (nodeData.actionType === ActionTypeEnum.SEND_SMS) {
      setSmsConfig({
        utmTerm,
      });
    }
  };

  const setSenderDetails = (sender: Sender) => {
    if (nodeData.actionType === ActionTypeEnum.SEND_EMAIL) {
      setEmailConfig({
        senderEmail: sender.SenderEmail,
        metaSenderEmail: sender.MetaSender,
      });
    }
  };

  const setPushTitle = (pushTitle: string) => {
    if (nodeData.actionType === ActionTypeEnum.SEND_PUSH_NOTIFICATION) {
      setPushConfig({
        title: pushTitle,
      });
    }
  };

  const setPushBody = (pushBody: string) => {
    if (nodeData.actionType === ActionTypeEnum.SEND_PUSH_NOTIFICATION) {
      setPushConfig({
        body: pushBody,
      });
    }
  };

  const setPushRedirectPath = (pushRedirectPath: string) => {
    if (nodeData.actionType === ActionTypeEnum.SEND_PUSH_NOTIFICATION) {
      setPushConfig({
        redirectPath: pushRedirectPath,
      });
    }
  };

  const setLeafbuyerSMSMarketingConfigId = (leafbuyerSMSMarketingConfigId: number) => {
    if (nodeData.actionType === ActionTypeEnum.SEND_SMS) {
      setSmsConfig({
        leafbuyerSMSMarketingConfigId: leafbuyerSMSMarketingConfigId === 0 ? null : leafbuyerSMSMarketingConfigId,
      });
    }
  };

  const setLeafbuyerMessageTemplateId = (templateId: string | null) => {
    if (nodeData.actionType === ActionTypeEnum.SEND_SMS) {
      setSmsConfig({
        leafbuyerMessageTemplateId: templateId,
      });
    }
  };

  const setPointBoost = (points: number) => {
    if (nodeData.actionType === ActionTypeEnum.GRANT_POINTS) {
      setPointBoostConfig({
        points: Number(points),
      });
    }
  };

  const senderOptions = useMemo(() => {
    if (!senders?.AllSenders) {
      return [];
    }
    return senders.AllSenders.map((sender) => ({
      label: `${sender.MetaSender} <${sender.SenderEmail}>`,
      id: sender.MarketingEmailSenderId || 0,
      data: sender,
    }));
  }, [senders]);

  const handleSenderChange = (option) => {
    if (option?.data) {
      setSenderDetails(option.data);
    }
  };

  const savePriorToTesting = async () => {
    try {
      await saveWorkflowCampaign(getSaveWorkflowCampaignPayload({}, builderState));
    } catch (err) {
      const error = err as ApiErrorResponse;
      notifications.error({ message: error?.response?.data?.Message ?? 'Failed to save campaign before sending test' });
      throw err;
    }
  };

  // Find the selected sender option based on the email and name for exact matching
  // Note: This is dumb - because Jake is doing stuff on the DB/API side while I am
  // making this change I can't "normalize SenderId as would be standard practice"
  // without stepping on his toes.
  const selectedSenderId = useMemo(() => {
    if (nodeData.actionType !== ActionTypeEnum.SEND_EMAIL) {
      return 0;
    }
    const matchingSender = senders?.AllSenders?.find(
      (sender) =>
        sender.SenderEmail === nodeData.config.emailConfig?.senderEmail &&
        sender.MetaSender === nodeData.config.emailConfig?.metaSenderEmail
    );
    return matchingSender?.MarketingEmailSenderId ?? 0;
  }, [senders, nodeData]);

  const defaultSenderValue = useMemo(() => {
    if (!senders) {
      return '';
    }

    if (senders.DefaultSender?.MetaSender) {
      return senders.DefaultSender.MetaSender;
    }

    if (senders.AllSenders?.[0]?.MetaSender) {
      return senders.AllSenders[0].MetaSender;
    }

    return '';
  }, [senders]);

  const actionTypeOptions = useMemo(
    () => [
      {
        label: 'Send email',
        id: ActionTypeEnum.SEND_EMAIL,
        disabled: !isEmailEnabled,
      },
      {
        label: 'Send push notification',
        id: ActionTypeEnum.SEND_PUSH_NOTIFICATION,
        disabled: !isPushEnabled,
      },
      {
        label: 'Send SMS',
        id: ActionTypeEnum.SEND_SMS,
        disabled: !isSMSEnabled,
      },
      {
        label: 'Grant point boost',
        id: ActionTypeEnum.GRANT_POINTS,
        disabled: !showLeafLogixLoyalty,
      },
    ],
    [isEmailEnabled, isSMSEnabled, isPushEnabled, showLeafLogixLoyalty]
  );

  const setActionType = (actionType: string) => {
    dispatch({
      type: 'set-node-data',
      payload: {
        nodeId: selectedNodeId,
        value: {
          actionType,
        },
      },
    });

    // initialize the config for the new action type
    if (actionType === ActionTypeEnum.SEND_EMAIL) {
      setEmailConfig({});
    } else if (actionType === ActionTypeEnum.SEND_PUSH_NOTIFICATION) {
      setPushConfig({});
    } else if (actionType === ActionTypeEnum.SEND_SMS) {
      setSmsConfig({});
    } else if (actionType === ActionTypeEnum.GRANT_POINTS) {
      setPointBoostConfig({});
    }
  };

  const getContentPreviewText = useMemo(() => {
    if (nodeData?.config.smsConfig && nodeData?.config.emailConfig) {
      return 'SMS and email content';
    }
    if (nodeData?.config.smsConfig) {
      return 'SMS content';
    }
    if (nodeData?.config.emailConfig) {
      return 'Email content';
    }
    return null;
  }, [nodeData?.config.smsConfig, nodeData?.config.emailConfig]);

  return (
    <>
      <SummarySidebarSection>
        <FormSection>
          <DropdownSingle
            automationId='campaign-workflow-builder-action-type-select'
            disabled={builderState.isReadOnly}
            helpText='Select an action'
            label='Action:'
            labelPlacement='top'
            options={actionTypeOptions}
            required
            setValue={(option) => setActionType(String(option.id))}
            value={nodeData.actionType || ''}
          />

          {nodeData.actionType === ActionTypeEnum.GRANT_POINTS && (
            <Input
              data-testid='grant-points-quantity'
              disabled={builderState.isReadOnly}
              endAdornment={<InputAdornment position='start'>POINTS</InputAdornment>}
              errorMessage={validationErrors.getError('config.pointBoostConfig.points')?.message}
              label='Point boost:'
              labelPlacement='top'
              placeholder='Add point boost'
              required
              type='number'
              value={nodeData.config.pointBoostConfig?.points || ''}
              onChange={(e) => setPointBoost(e.target.value)}
            />
          )}

          {nodeData.actionType === ActionTypeEnum.SEND_PUSH_NOTIFICATION && (
            <>
              <Input
                data-testid='push-title-input'
                disabled={builderState.isReadOnly}
                errorMessage={validationErrors.getError('config.pushConfig.title')?.message}
                label='Title:'
                labelPlacement='top'
                placeholder='Add push title'
                required
                value={nodeData.config.pushConfig?.title || ''}
                onChange={(e) => setPushTitle(e.target.value)}
              />
              <Input
                data-testid='push-body-input'
                disabled={builderState.isReadOnly}
                errorMessage={validationErrors.getError('config.pushConfig.body')?.message}
                label='Body:'
                labelPlacement='top'
                placeholder='Add push body'
                required
                value={nodeData.config.pushConfig?.body || ''}
                onChange={(e) => setPushBody(e.target.value)}
              />
              <DropdownSingle
                automationId='push-landing-page-select'
                disabled={builderState.isReadOnly}
                disableDefaultFooterContent
                errorMessage={validationErrors.getError('config.pushConfig.redirectPath')?.message}
                helpText='Select a landing page'
                label='App landing page:'
                labelPlacement='top'
                options={pushRedirectPathOptions}
                required
                setValue={(option) => setPushRedirectPath(option.id as string)}
                value={nodeData.config.pushConfig?.redirectPath || ''}
              />
              {!builderState.isReadOnly && (
                <Button
                  buttonSize='small'
                  label='Send Test'
                  variant='secondary'
                  onClick={async () => {
                    if (nodeData.actionType === ActionTypeEnum.SEND_PUSH_NOTIFICATION) {
                      if (!marketingCampaignId) {
                        notifications.error({ message: 'You must save the campaign before sending a test push' });
                        return;
                      }
                      await savePriorToTesting();
                      setShowSendTestPushModal(true);
                    }
                  }}
                />
              )}
            </>
          )}
        </FormSection>
      </SummarySidebarSection>
      {nodeData.actionType === ActionTypeEnum.SEND_EMAIL && (
        <SummarySidebarSection>
          <FormSection>
            <Input
              data-testid='email-subject-input'
              disabled={builderState.isReadOnly}
              errorMessage={validationErrors.getError('config.emailConfig.subjectLine')?.message}
              label='Subject:'
              labelPlacement='top'
              placeholder='Add email subject'
              required
              value={nodeData.config.emailConfig?.subjectLine || ''}
              onChange={(e) => setSubjectLine(e.target.value)}
            />
            <Input
              data-testid='email-preview-input'
              disabled={builderState.isReadOnly}
              errorMessage={validationErrors.getError('config.emailConfig.previewText')?.message}
              label='Preview text:'
              labelPlacement='top'
              placeholder='Add email preview text'
              required
              value={nodeData.config.emailConfig?.previewText || ''}
              onChange={(e) => setPreviewText(e.target.value)}
            />
            {useMessageLevelUtmFields && locProfile.MarketingCampaignAdvancedUtmFields && (
              <Input
                data-testid='email-utm-term-input'
                disabled={builderState.isReadOnly}
                label='UTM term:'
                labelPlacement='top'
                placeholder='Add email UTM term'
                required
                value={nodeData.config.emailConfig?.utmTerm || ''}
                onChange={(e) => setUtmTerm(e.target.value)}
              />
            )}
            {shouldShowSenderDropdown ? (
              <DropdownSingle
                disabled={builderState.isReadOnly}
                disableDefaultFooterContent
                errorMessage={validationErrors.getError('config.emailConfig.senderEmail')?.message}
                helpText='Select sender email'
                label='Sender email:'
                labelPlacement='top'
                options={senderOptions}
                required
                setValue={handleSenderChange}
                tooltip='Senders are configured in account settings. Please contact your account representative if you need to change them.'
                value={selectedSenderId || ''}
              />
            ) : (
              <Input
                disabled={builderState.isReadOnly}
                errorMessage={validationErrors.getError('config.emailConfig.senderEmail')?.message}
                label='Sender email:'
                labelPlacement='top'
                loading={sendersLoading}
                tooltip='This address is configured in account settings. Please contact your account representative if you need to change it.'
                value={defaultSenderValue}
              />
            )}
          </FormSection>
        </SummarySidebarSection>
      )}

      {nodeData.actionType === ActionTypeEnum.SEND_SMS && (
        <SummarySidebarSection>
          <FormSection>
            <DropdownSingle
              automationId='sms-brand-select'
              disabled={builderState.isReadOnly}
              helpText='Select a brand'
              label='Brand:'
              labelPlacement='top'
              options={
                leafbuyerSMSMarketingConfigs?.map((config) => ({
                  label: config.FriendlyName,
                  id: config.LeafbuyerSMSMarketingConfigId || 0,
                })) ?? []
              }
              required
              setValue={(option) => setLeafbuyerSMSMarketingConfigId(Number(option.id))}
              value={nodeData.config.smsConfig?.leafbuyerSMSMarketingConfigId || ''}
            />
            <Toggle
              automationId='sms-mms-checkbox'
              checked={nodeData.config.smsConfig?.isMMS}
              disabled={builderState.isReadOnly}
              gridColumns='4'
              label='MMS:'
              labelPlacement='start'
              tooltip='MMS messages (which include images) cost more, but generally drive higher user engagement'
              onChange={(e) => setSmsConfig({ isMMS: e.target.checked })}
            />
            <DropdownSingle
              automationId='sms-template-select'
              disabled={builderState.isReadOnly}
              helpText='Select a message template'
              label='Message template:'
              labelPlacement='top'
              options={smsTemplates?.map((template) => ({ label: template.Label, id: template.TemplateId })) ?? []}
              setValue={(option) => setLeafbuyerMessageTemplateId(String(option.id))}
              value={nodeData.config.smsConfig?.leafbuyerMessageTemplateId || ''}
            />
            {useMessageLevelUtmFields && locProfile.MarketingCampaignAdvancedUtmFields && (
              <Input
                data-testid='sms-utm-term-input'
                disabled={builderState.isReadOnly}
                label='UTM term:'
                labelPlacement='top'
                placeholder='Add SMS UTM term'
                required
                value={nodeData.config.smsConfig?.utmTerm || ''}
                onChange={(e) => setUtmTerm(e.target.value)}
              />
            )}
          </FormSection>
        </SummarySidebarSection>
      )}

      {(nodeData.actionType === ActionTypeEnum.SEND_SMS || nodeData.actionType === ActionTypeEnum.SEND_EMAIL) && (
        <SummarySidebarSection>
          <SideBarSectionHeaderContainer>
            <SideBarSectionHeader>{getContentPreviewText}</SideBarSectionHeader>
            {!builderState.isReadOnly && (
              <>
                <Button
                  buttonSize='small'
                  label='Send Test'
                  variant='secondary'
                  onClick={async () => {
                    if (nodeData.actionType === ActionTypeEnum.SEND_EMAIL) {
                      if (!marketingCampaignId) {
                        notifications.error({ message: 'You must save the campaign before sending a test SMS' });
                        return;
                      }
                      await savePriorToTesting();
                      setShowSendTestEmailModal(true);
                    } else if (nodeData.actionType === ActionTypeEnum.SEND_SMS) {
                      if (!marketingCampaignId) {
                        notifications.error({ message: 'You must save the campaign before sending a test SMS' });
                        return;
                      }
                      await savePriorToTesting();
                      setShowSendTestSmsModal(true);
                    }
                  }}
                />
                <ButtonMarginLeft
                  buttonSize='small'
                  label='Open editor'
                  variant='primary'
                  onClick={() => setShowEditor(true)}
                />
              </>
            )}
          </SideBarSectionHeaderContainer>
          <FormSection>
            {nodeData.config.savedTemplateId ? (
              <EmailPreviewContainer>
                <EmailPreviewBody>
                  <EmailPreviewBodyContainer>
                    <iframe
                      frameBorder='0'
                      height='300px'
                      srcDoc={savedEmailContent?.HtmlBody}
                      title='email-preview'
                      width='100%'
                    />
                  </EmailPreviewBodyContainer>
                </EmailPreviewBody>
              </EmailPreviewContainer>
            ) : (
              <NoContentBlock>
                <p>Content has not been created for this message yet.</p>
              </NoContentBlock>
            )}
          </FormSection>
        </SummarySidebarSection>
      )}

      <SendTestEmailModal
        isOpen={showSendTestEmailModal}
        onClose={() => setShowSendTestEmailModal(false)}
        onSendClick={(sendTo) => {
          if (!marketingCampaignId || !nodeData.config.emailConfig?.marketingEmailId) {
            setShowSendTestEmailModal(false);
            return;
          }
          sendTestEmailMutator(
            {
              MarketingCampaignId: marketingCampaignId,
              MarketingEmailId: nodeData.config.emailConfig?.marketingEmailId,
              RecipientEmail: sendTo,
            },
            {
              onSuccess: () => notifications.success({ message: 'Test email sent successfully' }),
              onError: (e) => {
                const err = e as ApiErrorResponse;
                notifications.error({ message: err.response.data.Message ?? 'Failed to send test email' });
              },
            }
          );
          setShowSendTestEmailModal(false);
        }}
      />
      <SendTestSMSModal
        isOpen={showSendTestSmsModal}
        onClose={() => setShowSendTestSmsModal(false)}
        onSendClick={(sendTo) => {
          if (!marketingCampaignId || !nodeData.config.smsConfig?.marketingSMSId) {
            setShowSendTestSmsModal(false);
            return;
          }
          sendTestSmsMutator(
            {
              MarketingCampaignId: marketingCampaignId,
              MarketingSMSId: nodeData.config.smsConfig?.marketingSMSId,
              PhoneNumber: sendTo,
            },
            {
              onSuccess: () => notifications.success({ message: 'Test SMS sent successfully' }),
              onError: (e) => {
                const err = e as ApiErrorResponse;
                notifications.error({ message: err.response.data.Message ?? 'Failed to send test SMS' });
              },
            }
          );
          setShowSendTestSmsModal(false);
        }}
      />
      <SendTestPushModal
        isOpen={showSendTestPushModal}
        onClose={() => setShowSendTestPushModal(false)}
        onSendClick={(sendTo) => {
          if (!marketingCampaignId || !nodeData.config.pushConfig?.marketingPushId) {
            debugger;
            setShowSendTestPushModal(false);
            return;
          }
          sendTestPushMutator(
            {
              MarketingCampaignId: marketingCampaignId,
              MarketingPushId: nodeData.config.pushConfig?.marketingPushId,
              RecipientEmail: sendTo,
            },
            {
              onSuccess: () => notifications.success({ message: 'Test push sent successfully' }),
              onError: (e) => {
                const err = e as ApiErrorResponse;
                notifications.error({ message: err.response.data.Message ?? 'Failed to send test push' });
              },
            }
          );
          setShowSendTestPushModal(false);
        }}
      />
    </>
  );
}

const EmailPreviewContainer = styled.div`
  width: 100%;
  background-color: var(--color-brand-primary-white);
  border-radius: var(--sizes-20);
  border: 1px solid var(--color-gray-20);
`;
const EmailPreviewBody = styled.div`
  width: 100%;
  padding: var(--sizes-40);
  display: flex;
  justify-content: center;
  align-items: center;
`;
const EmailPreviewBodyContainer = styled.div`
  width: 100%;
`;
